import { useMediaQuery, useTheme } from '@mui/material';

/**
 * Enhanced responsive utilities for comprehensive device support
 * Provides full responsive behavior for all screen sizes
 */
export const useResponsiveUtils = () => {
    const theme = useTheme();

    // Enhanced breakpoint detection with more granular control
    const isXs = useMediaQuery(theme.breakpoints.only('xs')); // 0-599px (Mobile Portrait)
    const isSm = useMediaQuery(theme.breakpoints.only('sm')); // 600-899px (Mobile Landscape/Small Tablet)
    const isMd = useMediaQuery(theme.breakpoints.only('md')); // 900-1199px (Tablet)
    const isLg = useMediaQuery(theme.breakpoints.only('lg')); // 1200-1535px (Desktop)
    const isXl = useMediaQuery(theme.breakpoints.only('xl')); // 1536px+ (Large Desktop)

    // Additional fine-grained breakpoints
    const isXxs = useMediaQuery('(max-width: 375px)'); // Very small phones
    const isSmallMobile = useMediaQuery('(max-width: 480px)'); // Small mobile
    const isMediumMobile = useMediaQuery('(min-width: 481px) and (max-width: 767px)'); // Medium mobile
    const isSmallTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)'); // Small tablet
    const isLargeTablet = useMediaQuery('(min-width: 1024px) and (max-width: 1279px)'); // Large tablet
    const isSmallDesktop = useMediaQuery('(min-width: 1280px) and (max-width: 1599px)'); // Small desktop
    const isLargeDesktop = useMediaQuery('(min-width: 1600px)'); // Large desktop

    // Range detection with enhanced logic
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-899px
    const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // >= 900px
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg')); // >= 1200px
    const isUltraWideScreen = useMediaQuery('(min-width: 1920px)'); // Ultra wide screens

    // Enhanced device type detection
    const deviceType = isXxs ? 'xxs-mobile' :
        isSmallMobile ? 'small-mobile' :
            isMediumMobile ? 'medium-mobile' :
                isSmallTablet ? 'small-tablet' :
                    isLargeTablet ? 'large-tablet' :
                        isSmallDesktop ? 'small-desktop' :
                            isLargeDesktop ? 'large-desktop' :
                                isMobile ? 'mobile' :
                                    isTablet ? 'tablet' : 'desktop';

    // Screen orientation detection
    const isPortrait = useMediaQuery('(orientation: portrait)');
    const isLandscape = useMediaQuery('(orientation: landscape)');

    // Touch device detection
    const isTouchDevice = useMediaQuery('(hover: none) and (pointer: coarse)');

    // Responsive value helper
    const getResponsiveValue = <T>(values: {
        xs?: T;
        sm?: T;
        md?: T;
        lg?: T;
        xl?: T;
    }): T => {
        if (isXl && values.xl !== undefined) return values.xl;
        if (isLg && values.lg !== undefined) return values.lg;
        if (isMd && values.md !== undefined) return values.md;
        if (isSm && values.sm !== undefined) return values.sm;
        if (isXs && values.xs !== undefined) return values.xs;

        // Fallback to first available value
        return values.xs || values.sm || values.md || values.lg || values.xl as T;
    };

    // Grid columns helper
    const getGridColumns = (config: {
        mobile?: number;
        tablet?: number;
        desktop?: number;
    }) => {
        if (isMobile) return config.mobile || 1;
        if (isTablet) return config.tablet || 2;
        return config.desktop || 3;
    };

    // Spacing helper
    const getSpacing = (size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl') => {
        const spacingMap = {
            xs: { xs: '0.5rem', sm: '0.75rem', md: '1rem' },
            sm: { xs: '0.75rem', sm: '1rem', md: '1.25rem' },
            md: { xs: '1rem', sm: '1.5rem', md: '2rem' },
            lg: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
            xl: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            xxl: { xs: '2.5rem', sm: '3rem', md: '4rem' },
        };

        return getResponsiveValue(spacingMap[size]);
    };

    // Font size helper
    const getFontSize = (type: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body' | 'caption') => {
        const fontMap = {
            h1: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem', lg: '3rem' },
            h2: { xs: '1.5rem', sm: '1.75rem', md: '2rem', lg: '2.25rem' },
            h3: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem', lg: '2rem' },
            h4: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem', lg: '1.75rem' },
            h5: { xs: '1rem', sm: '1.125rem', md: '1.25rem', lg: '1.5rem' },
            h6: { xs: '0.875rem', sm: '1rem', md: '1.125rem', lg: '1.25rem' },
            body: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
            caption: { xs: '0.625rem', sm: '0.75rem', md: '0.875rem' },
        };

        return getResponsiveValue(fontMap[type]);
    };

    // Container padding helper
    const getContainerPadding = (size: 'xs' | 'sm' | 'md' | 'lg' | 'xl') => {
        const paddingMap = {
            xs: { xs: '1rem', sm: '1.5rem', md: '2rem' },
            sm: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
            md: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            lg: { xs: '2.5rem', sm: '3rem', md: '4rem' },
            xl: { xs: '3rem', sm: '4rem', md: '5rem' },
        };

        return getResponsiveValue(paddingMap[size]);
    };

    // Card spacing helper
    const getCardSpacing = () => {
        return {
            padding: { xs: '1rem', sm: '1.5rem', md: '2rem' },
            margin: { xs: '0.5rem', sm: '1rem', md: '1.5rem' },
            gap: { xs: '1rem', sm: '1.5rem', md: '2rem' },
        };
    };

    // Enhanced table responsive helper with full device support
    const getTableResponsive = () => {
        return {
            // Responsive table widths
            minWidth: isXxs ? 280 :
                isSmallMobile ? 320 :
                    isMediumMobile ? 400 :
                        isSmallTablet ? 600 :
                            isLargeTablet ? 800 :
                                isDesktop ? 1000 : 1200,

            // Responsive font sizes
            fontSize: isXxs ? '0.875rem' :
                isSmallMobile ? '0.9rem' :
                    isMediumMobile ? '0.95rem' :
                        isSmallTablet ? '1rem' :
                            isLargeTablet ? '1.05rem' :
                                isDesktop ? '1.1rem' : '1.15rem',

            // Responsive padding
            cellPadding: isXxs ? '4px 6px' :
                isSmallMobile ? '6px 8px' :
                    isMediumMobile ? '8px 10px' :
                        isSmallTablet ? '10px 12px' :
                            isLargeTablet ? '12px 14px' :
                                isDesktop ? '14px 16px' : '16px 20px',

            // Responsive row height
            rowHeight: isXxs ? 32 :
                isSmallMobile ? 36 :
                    isMediumMobile ? 40 :
                        isSmallTablet ? 44 :
                            isLargeTablet ? 48 :
                                isDesktop ? 52 : 56,

            // Responsive header height
            headerHeight: isXxs ? 36 :
                isSmallMobile ? 40 :
                    isMediumMobile ? 44 :
                        isSmallTablet ? 48 :
                            isLargeTablet ? 52 :
                                isDesktop ? 56 : 60,
        };
    };

    // Enhanced button responsive helper
    const getButtonResponsive = (size: 'small' | 'medium' | 'large' = 'medium') => {
        const sizeMap = {
            small: {
                minHeight: isXxs ? 28 : isSmallMobile ? 30 : isMediumMobile ? 32 : isSmallTablet ? 34 : 36,
                padding: isXxs ? '4px 8px' : isSmallMobile ? '5px 10px' : isMediumMobile ? '6px 12px' : '8px 16px',
                fontSize: isXxs ? '0.7rem' : isSmallMobile ? '0.75rem' : isMediumMobile ? '0.8rem' : '0.875rem',
            },
            medium: {
                minHeight: isXxs ? 32 : isSmallMobile ? 36 : isMediumMobile ? 40 : isSmallTablet ? 44 : 48,
                padding: isXxs ? '6px 12px' : isSmallMobile ? '8px 16px' : isMediumMobile ? '10px 20px' : '12px 24px',
                fontSize: isXxs ? '0.75rem' : isSmallMobile ? '0.8rem' : isMediumMobile ? '0.875rem' : '1rem',
            },
            large: {
                minHeight: isXxs ? 36 : isSmallMobile ? 40 : isMediumMobile ? 44 : isSmallTablet ? 48 : 52,
                padding: isXxs ? '8px 16px' : isSmallMobile ? '10px 20px' : isMediumMobile ? '12px 24px' : '14px 28px',
                fontSize: isXxs ? '0.8rem' : isSmallMobile ? '0.875rem' : isMediumMobile ? '1rem' : '1.125rem',
            }
        };
        return sizeMap[size];
    };

    // Card responsive helper with enhanced spacing
    const getCardResponsive = () => {
        return {
            padding: isXxs ? '8px' :
                isSmallMobile ? '12px' :
                    isMediumMobile ? '16px' :
                        isSmallTablet ? '20px' :
                            isLargeTablet ? '24px' :
                                isDesktop ? '28px' : '32px',

            borderRadius: isXxs ? '6px' :
                isSmallMobile ? '8px' :
                    isMediumMobile ? '10px' :
                        isSmallTablet ? '12px' : '16px',

            margin: isXxs ? '4px' :
                isSmallMobile ? '6px' :
                    isMediumMobile ? '8px' :
                        isSmallTablet ? '12px' :
                            isLargeTablet ? '16px' :
                                isDesktop ? '20px' : '24px',
        };
    };

    // Grid responsive helper for perfect column management
    const getGridResponsive = (maxColumns: number = 4) => {
        let columns = 1;

        if (isXxs) columns = 1;
        else if (isSmallMobile) columns = Math.min(1, maxColumns);
        else if (isMediumMobile) columns = Math.min(2, maxColumns);
        else if (isSmallTablet) columns = Math.min(2, maxColumns);
        else if (isLargeTablet) columns = Math.min(3, maxColumns);
        else if (isSmallDesktop) columns = Math.min(3, maxColumns);
        else if (isLargeDesktop) columns = Math.min(4, maxColumns);
        else columns = Math.min(maxColumns, 4);

        return {
            columns,
            gap: isXxs ? '8px' :
                isSmallMobile ? '12px' :
                    isMediumMobile ? '16px' :
                        isSmallTablet ? '20px' :
                            isLargeTablet ? '24px' :
                                isDesktop ? '28px' : '32px',
        };
    };

    // Enhanced spacing helper
    const getSpacingResponsive = (baseSize: number = 16) => {
        const multiplier = isXxs ? 0.5 :
            isSmallMobile ? 0.6 :
                isMediumMobile ? 0.75 :
                    isSmallTablet ? 0.85 :
                        isLargeTablet ? 0.95 :
                            isDesktop ? 1 : 1.1;

        return `${baseSize * multiplier}px`;
    };

    // Typography responsive helper
    const getTypographyResponsive = (variant: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2' | 'caption') => {
        const variantMap = {
            h1: {
                fontSize: isXxs ? '1.5rem' : isSmallMobile ? '1.75rem' : isMediumMobile ? '2rem' : isSmallTablet ? '2.25rem' : isLargeTablet ? '2.5rem' : '3rem',
                lineHeight: isXxs ? 1.2 : isSmallMobile ? 1.25 : 1.3
            },
            h2: {
                fontSize: isXxs ? '1.25rem' : isSmallMobile ? '1.5rem' : isMediumMobile ? '1.75rem' : isSmallTablet ? '2rem' : isLargeTablet ? '2.25rem' : '2.5rem',
                lineHeight: isXxs ? 1.2 : isSmallMobile ? 1.25 : 1.3
            },
            h3: {
                fontSize: isXxs ? '1.125rem' : isSmallMobile ? '1.25rem' : isMediumMobile ? '1.5rem' : isSmallTablet ? '1.75rem' : isLargeTablet ? '2rem' : '2.25rem',
                lineHeight: isXxs ? 1.25 : isSmallMobile ? 1.3 : 1.35
            },
            h4: {
                fontSize: isXxs ? '1rem' : isSmallMobile ? '1.125rem' : isMediumMobile ? '1.25rem' : isSmallTablet ? '1.5rem' : isLargeTablet ? '1.75rem' : '2rem',
                lineHeight: isXxs ? 1.3 : isSmallMobile ? 1.35 : 1.4
            },
            h5: {
                fontSize: isXxs ? '0.875rem' : isSmallMobile ? '1rem' : isMediumMobile ? '1.125rem' : isSmallTablet ? '1.25rem' : isLargeTablet ? '1.5rem' : '1.75rem',
                lineHeight: isXxs ? 1.35 : isSmallMobile ? 1.4 : 1.45
            },
            h6: {
                fontSize: isXxs ? '0.75rem' : isSmallMobile ? '0.875rem' : isMediumMobile ? '1rem' : isSmallTablet ? '1.125rem' : isLargeTablet ? '1.25rem' : '1.5rem',
                lineHeight: isXxs ? 1.4 : isSmallMobile ? 1.45 : 1.5
            },
            body1: {
                fontSize: isXxs ? '0.75rem' : isSmallMobile ? '0.8rem' : isMediumMobile ? '0.875rem' : isSmallTablet ? '0.9rem' : isLargeTablet ? '1rem' : '1.125rem',
                lineHeight: isXxs ? 1.4 : isSmallMobile ? 1.45 : 1.5
            },
            body2: {
                fontSize: isXxs ? '0.65rem' : isSmallMobile ? '0.7rem' : isMediumMobile ? '0.75rem' : isSmallTablet ? '0.8rem' : isLargeTablet ? '0.875rem' : '1rem',
                lineHeight: isXxs ? 1.45 : isSmallMobile ? 1.5 : 1.55
            },
            caption: {
                fontSize: isXxs ? '0.6rem' : isSmallMobile ? '0.65rem' : isMediumMobile ? '0.7rem' : isSmallTablet ? '0.75rem' : isLargeTablet ? '0.8rem' : '0.875rem',
                lineHeight: isXxs ? 1.5 : isSmallMobile ? 1.55 : 1.6
            },
        };
        return variantMap[variant];
    };

    return {
        // Enhanced breakpoint detection
        isXs,
        isSm,
        isMd,
        isLg,
        isXl,
        isXxs,
        isSmallMobile,
        isMediumMobile,
        isSmallTablet,
        isLargeTablet,
        isSmallDesktop,
        isLargeDesktop,
        isMobile,
        isTablet,
        isDesktop,
        isLargeScreen,
        isUltraWideScreen,
        deviceType,
        isPortrait,
        isLandscape,
        isTouchDevice,

        // Enhanced helper functions
        getResponsiveValue,
        getGridColumns,
        getSpacing,
        getFontSize,
        getContainerPadding,
        getCardSpacing,
        getTableResponsive,
        getButtonResponsive,
        getCardResponsive,
        getGridResponsive,
        getSpacingResponsive,
        getTypographyResponsive,

        // Enhanced breakpoint values
        breakpoints: {
            xxs: '0px',
            xs: '376px',
            sm: '481px',
            md: '768px',
            lg: '1024px',
            xl: '1280px',
            xxl: '1600px',
            ultrawide: '1920px',
        },

        // Device-specific breakpoints
        devices: {
            smallMobile: '(max-width: 480px)',
            mediumMobile: '(min-width: 481px) and (max-width: 767px)',
            smallTablet: '(min-width: 768px) and (max-width: 1023px)',
            largeTablet: '(min-width: 1024px) and (max-width: 1279px)',
            smallDesktop: '(min-width: 1280px) and (max-width: 1599px)',
            largeDesktop: '(min-width: 1600px)',
        },
    };
};

export default useResponsiveUtils;

